import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { CommandLoading } from 'cmdk';
import { CheckIcon, FilterXIcon, HandCoinsIcon } from 'lucide-react';

import { msCatalystApi } from '../../services/api/ms-catalyst-api.js';
import { cn } from '../../utils/cn.js';
import { filterQs } from '../../utils/filters.js';

import Error from '../feedback/error.jsx';
import Loading from '../feedback/loading.jsx';
import { Badge } from '../ui/badge.jsx';
import { Button } from '../ui/button.jsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command.jsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.jsx';
import { Separator } from '../ui/separator.jsx';

export function DataTableCampaignSelectFilter({
  column,
  title,
  table,
  tableFilterCol,
  valueCol,
  query,
}) {
  // Column shown/searched in the list vs. column stored as the filter value.
  // Filtering by ROWID avoids breaking on campaign names with special chars.
  const filterValueCol = valueCol || tableFilterCol;
  const selectedValues = new Set(column?.getFilterValue());
  const [search, setSearch] = useState('');

  const queryCampaigns = useQuery({
    queryKey: [`data_table_filters_campaigns`, search],
    queryFn: async () => {
      let notInQs = ``;

      if (query?.data?.length > 0) {
        const selectedValuesStr = query.data.map((item) => item.name).join(',');

        notInQs = `${filterQs(tableFilterCol, `not_in:${selectedValuesStr}`)}&`;
      }

      const endpoint =
        search === ''
          ? `/campaigns?${notInQs}limit=20&offset=0`
          : `/campaigns?${notInQs}${filterQs(tableFilterCol, `like:*${search}*`)}&limit=20&offset=0`;

      const { data: res } = await msCatalystApi.get(endpoint);

      return res.data;
    },
  });

  const isSelected = selectedValues.has(query.data?.[filterValueCol]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-10 border-dashed">
          <HandCoinsIcon />
          {title}

          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm p-1 px-2 font-normal lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size >= 1 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm p-1 font-normal"
                  >
                    {selectedValues.size} selecionado(s)
                  </Badge>
                ) : null}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[200px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="pesquisar..."
            onValueChange={(e) => {
              setSearch(e);
            }}
          />

          <CommandList>
            {queryCampaigns.isLoading ||
            queryCampaigns.isPending ||
            queryCampaigns.isRefetching ? (
              <CommandLoading className="py-4">
                <Loading loaderSize="10" />
              </CommandLoading>
            ) : queryCampaigns.isError ? (
              <CommandEmpty>
                <Error />
              </CommandEmpty>
            ) : !queryCampaigns.data || queryCampaigns.data?.length === 0 ? (
              <CommandEmpty>Não foram encontrados resultados.</CommandEmpty>
            ) : (
              <>
                {selectedValues.size > 0 && (
                  <>
                    <CommandGroup>
                      <CommandItem
                        className="justify-center text-center"
                        onSelect={() => {
                          column?.setFilterValue(undefined);
                        }}
                      >
                        <FilterXIcon className="mr-2 size-3" />
                        Limpar Filtros
                      </CommandItem>
                    </CommandGroup>
                    <CommandSeparator />
                  </>
                )}

                {!query.isLoading && query?.data && (
                  <CommandGroup heading="Ativa">
                    <CommandItem
                      value={query.data?.ROWID}
                      onSelect={() => {
                        // this needs to update something to change the actual query
                        if (isSelected) {
                          selectedValues.delete(query.data?.[filterValueCol]);
                        } else {
                          selectedValues.add(query.data?.[filterValueCol]);
                        }
                        const filterValues = Array.from(selectedValues);
                        column?.setFilterValue(
                          filterValues.length ? filterValues : undefined,
                        );
                      }}
                    >
                      <div
                        className={cn(
                          'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible',
                        )}
                      >
                        <CheckIcon className="size-4" />
                      </div>

                      <span>{query.data?.[tableFilterCol]}</span>
                    </CommandItem>
                  </CommandGroup>
                )}

                <CommandSeparator />

                <CommandGroup>
                  {queryCampaigns?.data?.map((option, i) => {
                    const isSelected = selectedValues.has(
                      option[filterValueCol],
                    );

                    if (option.ongoing) {
                      return null;
                    }

                    return (
                      <CommandItem
                        key={i}
                        value={option.ROWID}
                        onSelect={() => {
                          // this needs to update something to change the actual query
                          if (isSelected) {
                            selectedValues.delete(option[filterValueCol]);
                          } else {
                            selectedValues.add(option[filterValueCol]);
                          }
                          const filterValues = Array.from(selectedValues);
                          column?.setFilterValue(
                            filterValues.length ? filterValues : undefined,
                          );
                        }}
                      >
                        <div
                          className={cn(
                            'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'opacity-50 [&_svg]:invisible',
                          )}
                        >
                          <CheckIcon className="size-4" />
                        </div>

                        <span>{option[tableFilterCol]}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function DataTableSelectFilter({
  column,
  title,
  table,
  tableFilterCol,
  querySelect,
  icon,
}) {
  const selectedValues = new Set(column?.getFilterValue());
  const [search, setSearch] = useState('');

  const querySelectFilters = useQuery({
    queryKey: [`data_table_filters_${table}`, search],
    queryFn: async () => {
      const endpoint =
        search === ''
          ? `${table}?limit=20&offset=0`
          : `${table}?${filterQs(tableFilterCol, `like:*${search}*`)}&limit=20&offset=0`;

      const { data: res } = await msCatalystApi.get(endpoint);

      return res.data;
    },
    select: (data) => {
      if (querySelect) {
        return querySelect(data);
      } else {
        return data;
      }
    },
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-10 border-dashed">
          {icon}
          {title}

          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size >= 1 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedValues.size} selecionado(s)
                  </Badge>
                ) : null}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[200px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="pesquisar..."
            onValueChange={(e) => {
              setSearch(e);
            }}
          />

          <CommandList>
            {querySelectFilters.isLoading ||
            querySelectFilters.isPending ||
            querySelectFilters.isRefetching ? (
              <CommandLoading>
                <Loading />
              </CommandLoading>
            ) : querySelectFilters.isError ? (
              <CommandEmpty>
                <Error />
              </CommandEmpty>
            ) : !querySelectFilters.data ||
              querySelectFilters.data.length === 0 ? (
              <CommandEmpty>Não foram encontrados resultados.</CommandEmpty>
            ) : (
              <>
                {selectedValues.size > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => column?.setFilterValue(undefined)}
                        className="justify-center text-center"
                      >
                        <FilterXIcon className="mr-2 size-3" />
                        Limpar Filtros
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}

                <CommandGroup>
                  {querySelectFilters?.data?.map((option, i) => {
                    const isSelected = selectedValues.has(
                      option[tableFilterCol],
                    );

                    return (
                      <CommandItem
                        key={i}
                        value={option.ROWID}
                        onSelect={() => {
                          // this needs to update something to change the actual query
                          if (isSelected) {
                            selectedValues.delete(option[tableFilterCol]);
                          } else {
                            selectedValues.add(option[tableFilterCol]);
                          }
                          const filterValues = Array.from(selectedValues);
                          column?.setFilterValue(
                            filterValues.length ? filterValues : undefined,
                          );
                        }}
                      >
                        <div
                          className={cn(
                            'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'opacity-50 [&_svg]:invisible',
                          )}
                        >
                          <CheckIcon className="size-4" />
                        </div>

                        <span>{option[tableFilterCol]}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
